import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Loader2, CheckCircle, Smartphone } from 'lucide-react';
import { userService } from '../../_services';
import Resolved from '../../components/css/dark/img/true-icon.png';
import Rxresolved from '../../components/css/dark/img/rx-true-icon.png';
import Pagination from "@/components/ui/PaginationWrapper";
import Linkify from 'react-linkify';

function Notification({ text, closeNotification, openCreateNotification, cPage }) {
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState([]);
    const [notificationTemp, setNotificationTemp] = useState([]);
    const [activePage, setActivePage] = useState(1);
    const [currentPaging, setCurrentPaging] = useState(1);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [resolvingId, setResolvingId] = useState(null);
    const [isMobile, setIsMobile] = useState(false);

    // Detect mobile screen
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    }, []);

    const convertUTCDateToLocalDate = useCallback((serverdate) => {
        const dateLocal = new Date(serverdate);
        return new Intl.DateTimeFormat('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: '2-digit', 
            hour: '2-digit', 
            minute: '2-digit' 
        }).format(new Date(dateLocal.getTime() - dateLocal.getTimezoneOffset() * 60 * 1000));
    }, []);

    const terminatedPageChange = useCallback((pageNumber) => {
        try {
            // Create a shallow copy to avoid mutating the original array
            let userlistobj = [...notification];
            let usertemplistobj = userlistobj.length > 9 
                ? userlistobj.slice((pageNumber - 1) * 9, pageNumber * 9) 
                : userlistobj;
            setActivePage(pageNumber);
            setNotificationTemp(usertemplistobj);
            setCurrentPaging(pageNumber);
            setError('');
        } catch (error) {
            setError('Error');
        }
    }, [notification]);

    const fetchDataLocally = useCallback(async (UserType, UserEmail, FilterJson, cPageNum) => {
        try {
            const apidata = await userService.GetNotification(UserType, UserEmail, FilterJson);
            
            if (apidata.length > 0) {
                setNotification(apidata);
                
                if (apidata.length <= 9) {
                    setNotificationTemp(apidata);
                } else {
                    // Use slice instead of splice to avoid mutating the array
                    let usertemplistobj = apidata.slice(0, 9);
                    setNotificationTemp(usertemplistobj);
                }
                setLoading(false);
                setError('');

                // Handle pagination after data is set
                if (cPageNum !== undefined && cPageNum > 1) {
                    // Set active page and calculate the correct slice
                    setActivePage(cPageNum);
                    setCurrentPaging(cPageNum);
                    const startIndex = (cPageNum - 1) * 9;
                    const endIndex = cPageNum * 9;
                    const pageData = apidata.slice(startIndex, endIndex);
                    setNotificationTemp(pageData);
                } else if (cPage && cPage > 1) {
                    setActivePage(cPage);
                    setCurrentPaging(cPage);
                    const startIndex = (cPage - 1) * 9;
                    const endIndex = cPage * 9;
                    const pageData = apidata.slice(startIndex, endIndex);
                    setNotificationTemp(pageData);
                }
            } else {
                setError('No notification created so far');
                setLoading(false);
            }
        } catch (error) {
            setError('Error');
            setLoading(false);
        }
    }, [cPage]);

    useEffect(() => {
        if (userdata) {
            fetchDataLocally(userdata.userrole, userdata.email, '');
        }
    }, [userdata]); // Removed fetchDataLocally from dependencies to prevent re-fetching

    const makeNotificationResolved = useCallback(async (notId, UserType, UserEmail, FilterJson, cPageNum) => {
        try {
            const apidata = await userService.NotificationClosing(notId, UserType, UserEmail, FilterJson);
            
            if (apidata.length > 0) {
                if (apidata[0].statuscode == 1) {
                    setSuccessMessage(apidata[0].statusmessage);
                    setError('');
                    setResolvingId(null);
                    setTimeout(() => {
                        setSuccessMessage('');
                    }, 5000);
                    fetchDataLocally(UserType, UserEmail, '', cPageNum);
                } else {
                    setError(apidata[0].statusmessage);
                    setSuccessMessage('');
                    setResolvingId(null);
                }
            } else {
                setError('Error');
                setSuccessMessage('');
                setResolvingId(null);
            }
        } catch (error) {
            setError('Error');
            setResolvingId(null);
        }
    }, [fetchDataLocally]);

    const handleFlip = (notId) => {
        setResolvingId(notId);
        makeNotificationResolved(notId, userdata.userrole, userdata.email, '', currentPaging);
    };

    const createNotification = () => {
        closeNotification(activePage);
        openCreateNotification(activePage);
    };

    const componentDecorator = (href, text, key) => (
        <a className="text-primary hover:underline" href={href} key={key} target="_blank" rel="noopener noreferrer">
            {text}
        </a>
    );

    const isRxHacker = window.location.host.split('.').slice(-2).join('.').toLowerCase() === "rxhacker.com" ||
                       window.location.host.toLowerCase() === "localhost:5464" ||
                       window.location.host.toLowerCase() === "tempfilter.23byt.es" ||
                       window.location.host.toLowerCase() === "reporting.nulifespanrx.com";

    return (
        <Dialog open={true} onOpenChange={() => closeNotification(activePage)}>
            <DialogContent className="max-w-6xl h-[calc(100vh-3rem)]">
                {loading ? (
                    <div className="flex justify-center items-center h-full">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : isMobile ? (
                    <div className="flex flex-col items-center justify-center h-full px-4 text-center">
                        <Smartphone className="h-16 w-16 text-muted-foreground mb-4" />
                        <h3 className="text-lg font-semibold text-foreground mb-2">
                            Content Not Available
                        </h3>
                        <p className="text-sm text-muted-foreground">
                            Content not available for mobile screen. Please use a desktop or tablet device.
                        </p>
                    </div>
                ) : (
                    <div className="flex flex-col h-full">
                        <DialogHeader className="flex-shrink-0">
                            <DialogTitle>{text || 'Manage Notifications'}</DialogTitle>
                        </DialogHeader>

                        <Button 
                            onClick={createNotification}
                            className="bg-primary hover:bg-primary/90 w-auto self-start mb-4 flex-shrink-0"
                        >
                            Create new
                        </Button>

                        {notification && notification.length > 0 ? (
                            <>
                                <div className="flex-1 overflow-y-auto min-h-0 mt-4">
                                    <div className="border rounded-lg overflow-hidden">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Date & Time</TableHead>
                                                    <TableHead>User</TableHead>
                                                    <TableHead className="w-[40%]">Content</TableHead>
                                                    <TableHead className="text-center">Actions</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {notificationTemp.map((item, index) => (
                                                    <TableRow key={index}>
                                                        <TableCell className="whitespace-nowrap">
                                                            {convertUTCDateToLocalDate(item.publishedOn)}
                                                        </TableCell>
                                                        <TableCell>{item.userFor}</TableCell>
                                                        <TableCell>
                                                            <Linkify componentDecorator={componentDecorator}>
                                                                {item.not_Content}
                                                            </Linkify>
                                                        </TableCell>
                                                        <TableCell className="text-center">
                                                            {item.scheduleStatus !== "Upcoming" && !item.isClosed && (
                                                                <TooltipProvider>
                                                                    <Tooltip>
                                                                        <TooltipTrigger asChild>
                                                                            <Button
                                                                                variant="ghost"
                                                                                size="sm"
                                                                                onClick={() => handleFlip(item.not_Id)}
                                                                                disabled={resolvingId === item.not_Id}
                                                                                className="relative hover:bg-transparent"
                                                                            >
                                                                                {resolvingId === item.not_Id ? (
                                                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                                                ) : (
                                                                                    <img 
                                                                                        src={isRxHacker ? Rxresolved : Resolved} 
                                                                                        alt="Mark Resolved"
                                                                                        className="h-5 w-5"
                                                                                    />
                                                                                )}
                                                                            </Button>
                                                                        </TooltipTrigger>
                                                                        <TooltipContent>
                                                                            <p>Mark Resolved</p>
                                                                        </TooltipContent>
                                                                    </Tooltip>
                                                                </TooltipProvider>
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>

                                {notification.length > 9 && (
                                    <div className="flex-shrink-0 flex justify-center mt-4">
                                        <Pagination
                                            activePage={activePage}
                                            itemsCountPerPage={9}
                                            totalItemsCount={notification.length}
                                            pageRangeDisplayed={5}
                                            onChange={terminatedPageChange}
                                        />
                                    </div>
                                )}
                            </>
                        ) : null}

                        {successMessage && (
                            <div className="text-sm text-green-700 bg-green-50 p-3 rounded-md border border-green-200 mt-4 flex-shrink-0">
                                {successMessage}
                            </div>
                        )}

                        {error && (
                            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md mt-4 flex-shrink-0">
                                {error}
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default Notification;
